import {inject, Injectable} from '@angular/core';
import {ColumnParamsStateService} from "../../../services/table-services/column-params-state.service";
import {UserPreferencesService} from "../../../services/user-preferences.service";
import {UserPreferences} from "../../../types/user-preferences.types";
import {RecordData, ColumnsForToggling} from "../../../types/records-table.types";
import {ApplyColumnsParams, ApplyUserColumnPreferencesParams} from "../../../types/table-types/records-table-view.types";
import {dropdownSettingsResources} from "../../../models/resource/tables/dropdown-settings-resource-type";
import {bloggerModalResources} from "../../../models/resource/tables/blogger-modal-resource-type";
import {SortOrder} from "../../../types/utility.types";
import {TableSortColumn} from "../types/table-sort-column.types";

@Injectable({
  providedIn: 'root'
})
export class RecordsTableViewService {
  private userPreferencesService: UserPreferencesService = inject(UserPreferencesService);
  private columnParamsStateService: ColumnParamsStateService = inject(ColumnParamsStateService);

  public applyUserColumnPreferences({ key, isModalOpen }: ApplyUserColumnPreferencesParams): void {
    if (isModalOpen) { return; }

    const userPreferences: UserPreferences | null = this.userPreferencesService.getUserPreferences();
    this.applyStickyColumns({ key, userPreferences });
    this.applyHiddenGroups({ key, userPreferences });
    this.applyHiddenColumns({ key, userPreferences });
  }

  public checkIsNewRecordDataValid(key: string, newRecordData: Partial<RecordData>): boolean {
    const columnParams = this.columnParamsStateService.getColumnParamsByKey(key);
    if (!columnParams) { return false; }

    for (let param of columnParams) {
      if (!param.required) { continue; }
      if (!newRecordData[param.key]) { return false; }
    }

    return true;
  }

  public updateWrapperStyles(resourceParamsKey: string, currentTableZoom: number): Record<string, string> {
    if (Object.keys(dropdownSettingsResources).includes(resourceParamsKey)) { return {}; }

    const scaleFactor = 100 / currentTableZoom;
    const scaledWidthHeight = 100 * scaleFactor;
    const height = !Object.keys(bloggerModalResources).includes(resourceParamsKey) ?
      `${scaledWidthHeight}%` :
      `${330 * scaleFactor}px`

    return  {
      transform: `scale(${currentTableZoom / 100})`,
      minWidth: `${scaledWidthHeight}%`,
      width: `${scaledWidthHeight}%`,
      height: `${height}`
    };
  }

  public setSortingColumnByUserPreferences(tableSortColumn: TableSortColumn): TableSortColumn {
    const userPreferences: UserPreferences | null = this.userPreferencesService.getUserPreferences();
    const sortParams: string | Array<string> | undefined = userPreferences?.sortParams;
    if (!sortParams) { return this.resetSorting(tableSortColumn); }

    const [sortParam, sortOrder] = typeof sortParams === 'string' ?
      sortParams.split(',') :
      sortParams[0].split(',');

    return {
      currentKeySortParam: sortParam,
      currentSortOrder: sortOrder as SortOrder
    };
  }

  private applyStickyColumns({ key, userPreferences }: ApplyColumnsParams): void {
    const stickyColumnKeys: Array<string> = userPreferences?.stickyColumnKeys ?? [];
    if (!stickyColumnKeys.length) { return; }

    stickyColumnKeys.forEach(stickyColumnKey => {
      const stickyColumn = this.columnParamsStateService.getColumnParamsByKey(key)?.find(column => column.key === stickyColumnKey);
      stickyColumn && this.columnParamsStateService.updateSticky(key, stickyColumn);
    });
  }

  private applyHiddenGroups({ key, userPreferences }: ApplyColumnsParams): void {
    const hiddenGroupsIds: Array<string> = userPreferences?.hiddenGroupsKeys ?? [];
    if (!hiddenGroupsIds?.length) { return; }

    const columnsForToggling: ColumnsForToggling = {};
    hiddenGroupsIds.forEach(groupId => {
      const hiddenColumns = this.columnParamsStateService.getColumnParamsByKey(key)?.filter(column => column.columnProperties.GROUP?.id === groupId);
      if (hiddenColumns?.length) {
        columnsForToggling[groupId] = hiddenColumns;
      }
    });

    columnsForToggling && this.columnParamsStateService.updateHiddenGroup(key, columnsForToggling)
  }

  private applyHiddenColumns({ key, userPreferences }: ApplyColumnsParams): void {
    const profiles = userPreferences?.columnProfiles ?? [];
    const activeProfile = profiles.find(i => i.isActive);
    const hiddenColumnKeys: Array<string> = activeProfile?.hiddenColumnKeys ?? [];

    if (userPreferences?.hiddenColumnKeys?.length) {
      this.userPreferencesService.saveUserPreferences({ hiddenColumnKeys: [] });
    }

    if (!hiddenColumnKeys?.length) { return; }
    this.columnParamsStateService.updateHiddenColumn({
      key,
      columnKeys: hiddenColumnKeys,
      profileName: activeProfile?.profileName
    });
  }

  private resetSorting(tableSortColumn: TableSortColumn): TableSortColumn {
    if (tableSortColumn.currentKeySortParam) { return tableSortColumn; }

    return {
      currentSortOrder: 'none',
      currentKeySortParam: ''
    };
  }
}
